import { Box, Button, Flex, Loading, Text, createColumnHelper } from 'ui'
import { useMemo } from 'react'
import { usePulseContext } from '../../context/PulseProvider'
import { useRatingsByLocation } from 'queries/pulse/useRatingsByLocation'
import { RatingsByLocation as RatingsByLocationType } from 'api/pulse/ratingsByLocation'
import { InfiniteTable } from '../InfiniteTable'
import { formatNumber } from 'lib/string'
import { useRatingsByLocationDownloadCsv } from 'queries/pulse/useRatingsByLocationDownloadCsv'

const columnHelper = createColumnHelper<RatingsByLocationType[0]>()

const columns = [
  columnHelper.accessor('company_name', {
    header: 'Location Name',
    cell: (info) => <Text>{info.getValue()}</Text>,
  }),
  columnHelper.accessor('count_shift_rating_by_worker', {
    header: '# Ratings by Worker to Shift',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatNumber(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('avg_shift_rating_by_worker', {
    header: 'Avg Rating by Worker to Shift',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? `${formatNumber(info.getValue() as number, 2)} / 5`
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('number_workers_rated_shift_below_3', {
    header: '# Workers Rated Shift Below 3',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatNumber(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('count_worker_rating_by_shift', {
    header: '# Ratings by Manager to Worker',
    cell: (info) => <Text>{formatNumber(info.getValue() as number)}</Text>,
  }),
  columnHelper.accessor('avg_worker_rating_by_shift', {
    header: 'Avg Rating by Manager to Worker',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? `${formatNumber(info.getValue() as number, 2)} / 5`
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('number_shifts_rated_worker_below_3', {
    header: '# Shifts Rated Worker Below 3',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatNumber(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
]

export function RatingsByLocation() {
  const {
    state: { filters },
  } = usePulseContext()

  const filtersWithPagination = useMemo(() => {
    return {
      ...filters,
      page: 0,
      pageSize: 50,
    }
  }, [filters])

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useRatingsByLocation(filtersWithPagination)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    useRatingsByLocationDownloadCsv(filters)

  const flatData = useMemo(() => {
    return data?.pages.flatMap((page) => page) ?? []
  }, [data])

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Breakdown by Location
          </Text>
          <Text fontSize={2} color="lightText">
            Breakdown of Shift and Worker ratings by location
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <InfiniteTable
          data={flatData}
          columns={columns}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          height="400px"
        />
      )}
    </Box>
  )
}
